import { c as _c } from "react/compiler-runtime";
import { IcoMaximize16, IcoMinimize16 } from '@onefootprint/icons';
import { AnimatePresence, motion, useSpring } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Button from '../button';
import TemplateEditor, { type TemplateEditorRef } from '../template-editor/template-editor';
import type { TemplateInputProps } from '../template-input/template-input';
export type TemplateTextareaProps = {
  value?: string;
  onChange?: (val: string) => void;
  height?: number;
  expandedHeight?: number;
  disabled?: boolean;
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  placeholder?: string;
  size?: 'default' | 'compact';
  hasError?: boolean;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};
export type TemplateTextareaRef = {
  focus: () => void;
};
const TemplateTextarea = forwardRef<TemplateTextareaRef, TemplateTextareaProps>((t0, ref) => {
  const $ = _c(37);
  const {
    value: t1,
    onChange,
    height: t2,
    expandedHeight,
    disabled: t3,
    autocompleteItems,
    placeholder,
    size: t4,
    hasError: t5,
    autoFocus,
    onFocus,
    onBlur
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const height = t2 === undefined ? 150 : t2;
  const disabled = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "compact" : t4;
  const hasError = t5 === undefined ? false : t5;
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => ({
      focus: () => {
        editorRef.current?.focus();
      }
    });
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  useImperativeHandle(ref, t6);
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      stiffness: 300,
      damping: 30
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const animatedHeight = useSpring(height, t7);
  const [currentHeight, setCurrentHeight] = useState(height);
  const shouldShowExpandButton = isHovered || isFocused;
  let t8;
  let t9;
  if ($[2] !== animatedHeight || $[3] !== expandedHeight || $[4] !== height || $[5] !== isExpanded) {
    t8 = () => {
      const targetHeight = isExpanded ? expandedHeight ?? height * 2 : height;
      animatedHeight.set(targetHeight);
    };
    t9 = [isExpanded, height, expandedHeight, animatedHeight];
    $[2] = animatedHeight;
    $[3] = expandedHeight;
    $[4] = height;
    $[5] = isExpanded;
    $[6] = t8;
    $[7] = t9;
  } else {
    t8 = $[6];
    t9 = $[7];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[8] !== animatedHeight) {
    t10 = () => {
      const unsubscribe = animatedHeight.on("change", latest => {
        setCurrentHeight(Math.round(latest));
      });
      return unsubscribe;
    };
    t11 = [animatedHeight];
    $[8] = animatedHeight;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsHovered(true);
    t13 = () => setIsHovered(false);
    $[11] = t12;
    $[12] = t13;
  } else {
    t12 = $[11];
    t13 = $[12];
  }
  let t14;
  if ($[13] !== isExpanded || $[14] !== shouldShowExpandButton) {
    t14 = shouldShowExpandButton && <motion.div className="absolute bottom-0 left-0 z-100 bg-transparent w-full p-1 rounded-b-md pointer-events-auto!" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }}><Button prefixIcon={isExpanded ? IcoMinimize16 : IcoMaximize16} variant="secondary" size="compact" type="button" onClick={() => setIsExpanded(!isExpanded)} className="bg-transparent! border-0! shadow-none!">{isExpanded ? "Collapse" : "Expand"}</Button></motion.div>;
    $[13] = isExpanded;
    $[14] = shouldShowExpandButton;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t14) {
    t15 = <AnimatePresence>{t14}</AnimatePresence>;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== onFocus) {
    t16 = () => {
      setIsFocused(true);
      onFocus?.();
    };
    $[18] = onFocus;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== onBlur) {
    t17 = () => {
      setIsFocused(false);
      onBlur?.();
    };
    $[20] = onBlur;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== autoFocus || $[23] !== autocompleteItems || $[24] !== currentHeight || $[25] !== disabled || $[26] !== hasError || $[27] !== onChange || $[28] !== placeholder || $[29] !== size || $[30] !== t16 || $[31] !== t17 || $[32] !== value) {
    t18 = <TemplateEditor ref={editorRef} height={currentHeight} disabled={disabled} placeholder={placeholder} size={size} value={value} onChange={onChange} variant="textarea" autocompleteItems={autocompleteItems} hasError={hasError} autoFocus={autoFocus} bottomPadding={48} onFocus={t16} onBlur={t17} />;
    $[22] = autoFocus;
    $[23] = autocompleteItems;
    $[24] = currentHeight;
    $[25] = disabled;
    $[26] = hasError;
    $[27] = onChange;
    $[28] = placeholder;
    $[29] = size;
    $[30] = t16;
    $[31] = t17;
    $[32] = value;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t15 || $[35] !== t18) {
    t19 = <div className="overflow-visible w-full relative" onMouseEnter={t12} onMouseLeave={t13}>{t15}{t18}</div>;
    $[34] = t15;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
});
TemplateTextarea.displayName = 'TemplateTextarea';
export default TemplateTextarea;